// Firestore configuration and helper functions
import { 
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase";

// Collection names - cada tipo de contenido tiene su propia colección
export const COLLECTIONS = {
  BLOG_POSTS: "blogPosts",
  INFOGRAPHIC_POSTS: "infographicPosts", 
  CONFERENCES: "conferences",
  PROJECTS: "projects",
  VIDEOS: "videos",
  PODCAST_PODS: "podcastPods",
  BOOKS: "books",
  QUOTES: "quotes",
  TALKS: "talks",
  PERSONAL_INFO: "personal_info",
  SERVICES: "services",
  TESTIMONIALS: "testimonials",
  CONTACTS: "contacts", // For future CRM
  IDEAS: "ideas", // For future idea bank
  USERS: "users" // For admin users
};

// Generic Firestore operations
export const firestoreService = {
  // Get all documents from a collection
  async getAll(collectionName, options = {}) {
    try {
      let q = collection(db, collectionName);
      
      // Apply filters
      if (options.where) {
        options.where.forEach(filter => {
          q = query(q, where(filter.field, filter.operator, filter.value));
        });
      }
      
      // Apply ordering
      if (options.orderBy) {
        q = query(q, orderBy(options.orderBy.field, options.orderBy.direction || 'desc'));
      }
      
      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error getting documents from ${collectionName}:`, error);
      throw error;
    }
  },

  // Get a single document
  async getById(collectionName, id) {
    try {
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error(`Error getting document ${id} from ${collectionName}:`, error);
      throw error;
    }
  },

  // Add a new document
  async add(collectionName, data) {
    try {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error(`Error adding document to ${collectionName}:`, error);
      throw error;
    }
  },

  // Update a document
  async update(collectionName, id, data) {
    try {
      const docRef = doc(db, collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error(`Error updating document ${id} in ${collectionName}:`, error);
      throw error;
    }
  },

  // Delete a document
  async delete(collectionName, id) {
    try {
      await deleteDoc(doc(db, collectionName, id));
      return true;
    } catch (error) {
      console.error(`Error deleting document ${id} from ${collectionName}:`, error);
      throw error;
    }
  },

  // Batch operations
  async batch(operations) {
    try {
      const batch = writeBatch(db);
      
      operations.forEach(operation => {
        const { type, collection: collectionName, id, data } = operation;
        const docRef = doc(db, collectionName, id);
        
        switch (type) {
          case 'set':
            batch.set(docRef, { ...data, updatedAt: serverTimestamp() });
            break;
          case 'update':
            batch.update(docRef, { ...data, updatedAt: serverTimestamp() });
            break;
          case 'delete':
            batch.delete(docRef);
            break;
          default:
            console.warn(`Unknown batch operation type: ${type}`);
        }
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error executing batch operations:', error);
      throw error;
    }
  }
};

// Specific service functions for different content types
export const blogPostsService = {
  async getAllBlogPosts() {
    const allPosts = await firestoreService.getAll(COLLECTIONS.BLOG_POSTS, {
      orderBy: { field: 'createdAt', direction: 'desc' }
    });
    
    // Filtrar posts que no están en borrador
    // Si el campo 'draft' no existe, se considera publicado (compatibilidad con posts antiguos)
    return allPosts.filter(post => {
      // Si no tiene campo 'draft', se considera publicado
      if (post.draft === undefined) {
        return true;
      }
      // Si tiene campo 'draft', solo mostrar si es false (publicado)
      return post.draft === false;
    });
  },

  async getBlogPostBySlug(slug) {
    const posts = await firestoreService.getAll(COLLECTIONS.BLOG_POSTS, {
      where: [{ field: 'slug', operator: '==', value: slug }],
      limit: 1
    });
    
    if (posts.length === 0) {
      return null;
    }
    
    const post = posts[0];
    
    // Verificar si el post está en borrador
    // Si no tiene campo 'draft', se considera publicado (compatibilidad con posts antiguos)
    if (post.draft === undefined) {
      return post; // Post antiguo, se considera publicado
    }
    
    // Si tiene campo 'draft', solo devolver si es false (publicado)
    return post.draft === false ? post : null;
  }
};

export const infographicPostsService = {
  async getAllInfographicPosts() {
    return firestoreService.getAll(COLLECTIONS.INFOGRAPHIC_POSTS, {
      orderBy: { field: 'date', direction: 'desc' }
    });
  }
};

export const conferencesService = {
  async getAllConferences() {
    return firestoreService.getAll(COLLECTIONS.CONFERENCES, {
      orderBy: { field: 'date', direction: 'desc' }
    });
  }
};

export const projectsService = {
  async getAllProjects() {
    return firestoreService.getAll(COLLECTIONS.PROJECTS, {
      orderBy: { field: 'date', direction: 'desc' }
    });
  }
};

export const videosService = {
  async getAllVideos() {
    return firestoreService.getAll(COLLECTIONS.VIDEOS, {
      orderBy: { field: 'date', direction: 'desc' }
    });
  }
};

export const podcastPodsService = {
  async getAllPodcastPods() {
    return firestoreService.getAll(COLLECTIONS.PODCAST_PODS, {
      orderBy: { field: 'date', direction: 'desc' }
    });
  }
};

export const booksService = {
  async getAllBooks() {
    return firestoreService.getAll(COLLECTIONS.BOOKS, {
      orderBy: { field: 'createdAt', direction: 'desc' }
    });
  }
};

export const quotesService = {
  async getAllQuotes() {
    return firestoreService.getAll(COLLECTIONS.QUOTES, {
      orderBy: { field: 'createdAt', direction: 'desc' }
    });
  }
};

export const talksService = {
  async getAllTalks() {
    return firestoreService.getAll(COLLECTIONS.TALKS, {
      orderBy: { field: 'date', direction: 'desc' }
    });
  }
};

export const personalInfoService = {
  async getPersonalInfo() {
    return firestoreService.getAll(COLLECTIONS.PERSONAL_INFO, {
      orderBy: { field: 'order', direction: 'asc' }
    });
  },

  async getPersonalInfoBySection(section) {
    const info = await firestoreService.getAll(COLLECTIONS.PERSONAL_INFO, {
      where: [{ field: 'section', operator: '==', value: section }],
      limit: 1
    });
    return info.length > 0 ? info[0] : null;
  }
};

export const servicesService = {
  async getAllServices() {
    return firestoreService.getAll(COLLECTIONS.SERVICES, {
      where: [{ field: 'active', operator: '==', value: true }],
      orderBy: { field: 'order', direction: 'asc' }
    });
  }
};

